import React, {useCallback} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Box from "@mui/material/Box";
import CircularProgress from '@mui/material/CircularProgress';
import highcharts_more from 'highcharts/highcharts-more';
import dumbbell from 'highcharts/modules/dumbbell'
import lollipop from 'highcharts/modules/lollipop'
import { buildChartTooltip, updateChartTheme } from "../../../utils/helpsers";
import logoSignature from '../../../IB_Signature.png';
import PropTypes from "prop-types";
import { LollipopStyles } from "./LollipopChart.styles";
import noData from "highcharts/modules/no-data-to-display"

// Load Highcharts modules
highcharts_more(Highcharts);
noData(Highcharts)
dumbbell(Highcharts);
lollipop(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);


/**
 * Histogram chart using highcharts library.
 * @param {string} title - The title of the chart.
 * @param {string} description - Description of the chart.
 * @param {string} id - A unique name for the chart.
 * @param {array, object, string} data - data to be rendered in the chart.
 * @param {array} ranges - An array with two values.
 * @param {array} customRanges - An array with two custom values.
 */

const LollipopChart = ({ data, title, description, id, ranges = null, customRanges = null}) => {
  let axisTitle = description ? `<span style="position: relative">${title} <span class="info-icon" id="${id}" style="font-size: 14px; fill:#773dbd"><b>ⓘ</b></span><span class="tooltip-desc" style="visibility: hidden">${description}</span></span>` : title;

  const attachTooltipDescription = useCallback(()=> {
    if (description) {
      let infoIcon = document.querySelector(`.highcharts-axis #${id}`);
      if (infoIcon) {
        buildChartTooltip({ infoIcon: infoIcon, styles:"left: 100%; transform-origin: top left; transform: rotate(90deg);" });
      }
    }
  },[description, id])

  Highcharts.setOptions({
    lang: {
      numericSymbols: [' k ', ' M ', ' B ']
    }
  });

  let logoCredit;
  const Data = {
    chart: {
      type: "lollipop",
      height: 360,
      backgroundColor: '#fff',
      style: {
        fontFamily: ['Titillium Web', 'sans-serif']
      },
      events: {
        load: function () {
          if (this.options.chart.forExport) {
            this.renderer.image(`${logoSignature}`, 10, 350, 100, 10).add();
          }
        },
        render: function (){
          // Building Tooltips
          attachTooltipDescription();
        },
        beforePrint: function () {
          logoCredit = this.renderer.image(`${logoSignature}`, 10, 350, 100, 10).add();
          this.print();
        },
        afterPrint: function () {
          logoCredit.element.remove();
        },
      },
    },
    title: {
      text: "",
    },
    subtitle: {
      text: "",
    },
    xAxis: {
      categories: data.categories,
      crosshair: true,
      labels: {
        enabled: false,
      },
    },
    yAxis: {
      max: ranges && ranges[1] ? +(ranges[1]).toFixed(2) : null,
      min: ranges && ranges[0] ? +(ranges[0]).toFixed(2) : null,
      color: '#773DBD',
      title: {
        enabled: true,
        text: axisTitle,
        useHTML: true,
      },
      plotLines: [{
        dashStyle: 'shortdash',
        value: customRanges ? customRanges[0] : null,
        width: 2,
        color: '#00D7BE',
        zIndex: "10",
      }, {
        dashStyle: 'shortdash',
        value: customRanges && customRanges[1] ? customRanges[1] : null,
        width: 2,
        color: '#00D7BE',
        zIndex: "10",
      }],
    },
    tooltip: {
      headerFormat: '<span style="font-size:12px"><b>{point.key}</b></span><table>',
      pointFormat:
        '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y}</b></td></tr>',
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
      backgroundColor: '#FFF',
    },
    plotOptions: {
      lollipop: {
        marker: {
          radius: 3,
          states: {
            select: {
              fillColor: '#F5B335',
              lineColor: '#F5B335',
              radius: 5,
            }
          }
        },
      },
    },
    exporting: {
      tableCaption: false,
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            {
              text: "Toggle Light/Dark mode",
              onclick: function () {
                updateChartTheme(this);
              }
            },
            {
              separator: true,
            },
            {
              text: "Download PDF document",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  `Downloaded: PDF in ${title} chart`,
                ]);
                this.exportChart({
                  type: "application/pdf",
                });
              },
            },
            {
              text: "Download CSV",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  `Downloaded: CSV in ${title} chart`,
                ]);
                this.downloadCSV();
              },
            },
            {
              text: "Download XLS",
              onclick: function () {
                window._paq.push([
                  "trackEvent",
                  "Board DistrictExplorer",
                  `Downloaded: XLS in ${title} chart`,
                ]);
                this.downloadXLS();
              },
            },
            {
              separator: true,
            },
            {
              text: 'Toggle Raw Data Table',
              onclick: function() {
                if (this.dataTableDiv && this.dataTableDiv.style.display !== 'none') {
                  this.dataTableDiv.style.display = 'none';
                } else {
                  this.viewData();
                  this.dataTableDiv.style.display = '';
                }
              }
            }
          ],
        },
      },
      chartOptions: {
        chart: {
          backgroundColor: '#fff'
        }
      }
    },
    series: [
      {
        showInLegend: false,
        name: "Data",
        data: data.data,
        color: '#773DBD',
      },
    ],
  };

  switch (data) {
    case ('loading'):
      return (
        <Box sx={LollipopStyles}>
          <CircularProgress size={80}/>
        </Box>
      );
    case ('no-data'):
      return <div className="no-data">(Data unavailable)</div>
    default:
      return <HighchartsReact
        highcharts={Highcharts}
        options={Data}
      />
  }
};

LollipopChart.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  description: PropTypes.string,
  data: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
    PropTypes.string.isRequired,
  ]),
  ranges: PropTypes.array,
  customRanges: PropTypes.array,
}

export default React.memo(LollipopChart);
